"use client";
import { useFileUpload } from "@libs/customHooks/useFileUpload";
import { useFolderUpload } from "@libs/customHooks/useFolderUpload";
import { fileTypeImages } from "@libs/helper/fileTypeImages";
import { UploadProgressDrawer } from "@packages/ui/components";
import Image from "next/image";
import React, { ReactNode, useEffect, useRef, useState } from "react";
import { useData } from "./context";

interface props {
  children: ReactNode;
}

const RedirectProvider = ({ children }: props) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const folderInputRef = useRef<HTMLInputElement | null>(null);
  const [files, setFiles] = useState<any>([]);
  const [uploadType, setUploadType] = useState<"file" | "folder">("file");
  const [isUploading, setIsUploading] = useState(false);
  const [newFileUploadStatus, setNewFileUploadStatus] = useState(false);
  const { upload, setUpload } = useData();
  const {
    key,
    bucketType,
    parentId,
    apiEndPoint,
    projectId,
    onSuccess,
    dataRoomProps,
  } = upload || {};

  // Add upload queue to track pending uploads
  const uploadQueueRef = useRef<
    Array<{
      type: "file" | "folder";
      files: File[]; // Change to File[] instead of FileList for better handling
      displayFiles?: any[]; // Add displayFiles property to store UI display information
    }>
  >([]);
  const isProcessingRef = useRef(false);

  const { uploadFiles, fileStatuses, abortFileUpload, setFileStatuses } =
    useFileUpload({
      bucketType: bucketType,
      maxSize: 250,
    });

  const {
    handleFolderUpload,
    folderFileStatuses,
    setFileStatuses: setFolderFileStatuses,
    abortFolderFile,
    isUploading: isFolderUploading,
  } = useFolderUpload({
    bucketType: bucketType,
    apiEndPoint,
    onComplete: () => {
      onSuccess?.(null, null, "folder");
      processNextUpload();
      setUpload((prev: any) => ({
        ...prev,
        dataRoomProps: null,
      }));
    },
    dataRoomProps,
  });

  useEffect(() => {
    setUpload({ fileOpen: fileInputRef, folderOpen: folderInputRef });
  }, [fileInputRef, folderInputRef]);

  // Monitor file statuses to detect when file upload is complete
  useEffect(() => {
    if (uploadType === "file" && Object.keys(fileStatuses).length > 0) {
      const allCompleted = Object.values(fileStatuses).every(
        (status) => status.status === "success" || status.status === "error"
      );

      if (allCompleted && isUploading) {
        processNextUpload();
      }
    }
  }, [fileStatuses, uploadType, isUploading]);

  const handleAbortSingleFile = (fileName: string) => {
    if (uploadType === "file") {
      abortFileUpload(fileName);
    } else {
      // For folder uploads, abort individual file
      abortFolderFile(fileName);
    }
  };

  // Add a function to prepare file display entries
  const prepareFileEntries = (filesArray: File[], type: "file" | "folder") => {
    return filesArray.map((file) => {
      const pattern = /[^a-zA-Z0-9 _.-]/g;
      const fileExtension = file.name.split(".").pop() || "";
      const fileNameWithoutExtension = file.name.substring(
        0,
        file.name.lastIndexOf(".")
      );
      const fileName = fileNameWithoutExtension;
      const fullName = `${fileName}.${fileExtension}`;
      return {
        fileName: fullName,
        fileType: file.name.split(".").pop()?.toLowerCase() || "unknown",
        status: "pending", // Initial status for display
        progress: 0,
        fileWithPath: type === "folder" ? file.webkitRelativePath : undefined,
      };
    });
  };

  // Add a ref to keep track of all displayed files, including completed ones
  const allDisplayedFilesRef = useRef<any[]>([]);

  // Process next upload in the queue
  const processNextUpload = () => {
    // Mark current upload as complete
    setIsUploading(false);
    isProcessingRef.current = false;

    console.log(
      "Process next upload - queue length:",
      uploadQueueRef.current.length
    );

    // Store current files in our history ref before moving to next batch
    // This ensures we don't lose the completed files
    if (files.length > 0) {
      allDisplayedFilesRef.current = [...files];
    }

    // Check if there are more uploads in the queue
    if (uploadQueueRef.current.length > 0) {
      setTimeout(() => {
        const nextUpload = uploadQueueRef.current.shift();
        if (nextUpload) {
          console.log("Processing next upload of type:", nextUpload.type);

          // Update file display for the next batch - include previous files
          const currentFiles = nextUpload.displayFiles || [];
          // Combine arrays and remove duplicates based on fileName
          const combinedFiles = [
            ...currentFiles,
            ...allDisplayedFilesRef.current,
          ];
          const uniqueFiles = Array.from(
            new Map(combinedFiles.map((file) => [file.fileName, file])).values()
          );

          setFiles(uniqueFiles);

          processUpload(nextUpload.type, nextUpload.files);
        }
      }, 500); // Slightly longer delay to ensure UI updates properly
    } else {
      // Even if there are no more uploads, keep the current files visible
      // No need to update the files state here as we want to keep showing the current files
    }
  };

  // Process an upload
  const processUpload = async (
    type: "file" | "folder",
    selectedFiles: File[] | FileList
  ) => {
    if (!selectedFiles.length) return;

    console.log(
      `Starting to process ${selectedFiles.length} files of type: ${type}`
    );

    isProcessingRef.current = true;
    setIsUploading(true);
    setUploadType(type);
    setDrawerOpen(true);

    // Convert to array if it's a FileList
    const filesArray = Array.isArray(selectedFiles)
      ? selectedFiles
      : Array.from(selectedFiles);

    if (type === "file") {
      // Instead of replacing files, we'll keep the display files as is
      // They should already have been set when adding to queue or starting upload

      // Create a new FileList-like object for upload
      const dataTransfer = new DataTransfer();
      filesArray.forEach((file) => dataTransfer.items.add(file));

      const uploadedFiles = await uploadFiles(dataTransfer.files, key);
      setUpload((prev: any) => ({
        ...prev,
        selectedFiles: dataTransfer.files,
        uploadedFiles: uploadedFiles,
      }));

      if (uploadedFiles?.length! > 0) {
        onSuccess(uploadedFiles, dataTransfer.files, "file", parentId);
      }

      // Handle file upload completion here instead of relying only on the useEffect
      const allCompleted = Object.values(fileStatuses).every(
        (status) => status.status === "success" || status.status === "error"
      );

      if (allCompleted) {
        processNextUpload();
      }
    } else {
      // For folder upload, create a new FileList-like object
      const dataTransfer = new DataTransfer();
      filesArray.forEach((file) => dataTransfer.items.add(file));

      // Folder upload completion is handled in the useFolderUpload hook's onComplete callback
      await handleFolderUpload(dataTransfer.files, projectId, parentId, key);
    }

    // Reset input values
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (folderInputRef.current) folderInputRef.current.value = "";
  };

  const handleFilesSelected = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selectedFiles = event.target.files;
    if (!selectedFiles || selectedFiles.length === 0) return;

    console.log(
      `Selected ${selectedFiles.length} files, isUploading: ${isUploading}`
    );

    // Clone the files properly before resetting the input
    const filesArray = Array.from(selectedFiles);

    // Create file entries for display
    const fileEntries = prepareFileEntries(filesArray, "file");

    // If already uploading, add to queue instead of starting immediately
    if (isUploading) {
      console.log("Adding files to queue because upload is in progress");
      uploadQueueRef.current.push({
        type: "file",
        files: filesArray, // Store as array of File objects
        displayFiles: fileEntries, // Store display information
      });
      const uploadStatuses = fileEntries?.map((file) => ({
        // Default to aborted if we don't know the exact state
        [file?.fileName]: {
          status: "pending",
        },
      }));
      // Add new files to current display
      setFileStatuses((prev: any) => ({
        ...prev,
        ...Object.assign({}, ...uploadStatuses),
      }));
      // Add new files to current display
      setFiles((prev: any) => [...fileEntries, ...prev]);

      // Reset the input for next selection
      if (fileInputRef.current) fileInputRef.current.value = "";

      return;
    }

    // Reset the input
    if (fileInputRef.current) fileInputRef.current.value = "";

    // Set files for display
    // Preserve any existing files that might be in the display
    setFiles(fileEntries);

    await processUpload("file", filesArray);
  };

  const handleFolderSelected = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selectedFiles = event.target.files;
    if (!selectedFiles || selectedFiles.length === 0) return;

    console.log(
      `Selected ${selectedFiles.length} folder files, isUploading: ${isUploading}`
    );

    // Clone the files properly before resetting the input
    const filesArray = Array.from(selectedFiles);

    // Create file entries for display
    const fileEntries = prepareFileEntries(filesArray, "folder");

    // If already uploading, add to queue instead of starting immediately
    if (isUploading) {
      console.log("Adding folder to queue because upload is in progress");
      uploadQueueRef.current.push({
        type: "folder",
        files: filesArray, // Store as array of File objects
        displayFiles: fileEntries, // Store display information
      });
      const uploadStatuses = fileEntries?.map((file) => ({
        // Default to aborted if we don't know the exact state
        [file?.fileName]: {
          status: "pending",
        },
      }));
      // Add new files to current display
      setFolderFileStatuses((prev: any) => ({
        ...prev,
        ...Object.assign({}, ...uploadStatuses),
      }));
      // Add new files to current display
      setFiles((prev: any) => [...fileEntries, ...prev]);

      // Reset the input for next selection
      if (folderInputRef.current) folderInputRef.current.value = "";

      return;
    }

    // Reset the input
    if (folderInputRef.current) folderInputRef.current.value = "";

    // Set files for display - preserve existing files
    setFiles(fileEntries);

    await processUpload("folder", filesArray);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setFiles([]);
    allDisplayedFilesRef.current = []; // Clear history when drawer is closed
    setFileStatuses({});
    setFolderFileStatuses({});
    setUploadType("file");

    // Clear any pending uploads in the queue
    uploadQueueRef.current = [];
    isProcessingRef.current = false;
    setIsUploading(false);

    if (fileInputRef.current) fileInputRef.current.value = "";
    if (folderInputRef.current) folderInputRef.current.value = "";
  };

  return (
    <div>
      {children}
      {/* Hidden input for file and folder selection */}
      <input
        type="file"
        multiple
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFilesSelected}
      />

      <input
        type="file"
        webkitdirectory="true"
        directory=""
        multiple
        ref={folderInputRef}
        style={{ display: "none" }}
        onChange={handleFolderSelected}
        {...({ webkitdirectory: "", directory: "" } as any)}
      />
      <UploadProgressDrawer
        open={drawerOpen}
        onClose={handleDrawerClose}
        handleAbortSingleFile={handleAbortSingleFile}
        // handleAbortAllUploads={handleAbortAllUploads}
        files={files}
        getFileIcon={(fileType: any) => {
          const src = fileTypeImages[fileType] || fileTypeImages["img"];
          return <Image src={src} alt="file icon" width={16} height={16} />;
        }}
        fileStatuses={{ ...fileStatuses, ...folderFileStatuses }}
        // uploadType={uploadType}
        isUploading={isUploading}
      />
    </div>
  );
};

export default RedirectProvider;
